import {FormEvent, useEffect, useMemo, useRef, useState} from 'react';
import './ResetPasswordForm.css';
import './AuthenticationFields.css';
import {HolmesJrGuide} from './HolmesJrGuide';
import {lookupRecoveryToken, PASSWORD_REQUIREMENTS, passwordMeetsAllRequirements, passwordStrength} from './accountRecoveryTokens';

const STRENGTH_LABEL: Record<ReturnType<typeof passwordStrength>, string> = {
  too_weak: 'Too weak',
  fair: 'Fair',
  strong: 'Strong',
};

type Props = {
  /** Overrides fragment capture -- used when this screen is opened from AccountRecoveryWalkthrough.tsx's demo links. */
  token?: string;
  onRequestNewLink?: () => void;
  live?: boolean;
};

function captureRecoveryToken(): string|null {
  const match=/^#token=([A-Za-z0-9_-]{43})$/.exec(window.location.hash);
  const safeQuery=new URLSearchParams(window.location.search);
  safeQuery.delete('token');
  const query=safeQuery.toString();
  // Keep the bearer credential only in component memory. Fragments never reach the
  // server, and replaceState removes both the fragment and any rejected legacy query token.
  window.history.replaceState(window.history.state,'',`${window.location.pathname}${query?`?${query}`:''}`);
  return match?.[1]??null;
}

export function ResetPasswordForm({token: tokenProp, onRequestNewLink, live=false}: Props) {
  const [effectiveToken] = useState<string|null>(()=>tokenProp??(live?captureRecoveryToken():new URLSearchParams(window.location.search).get('token')));
  const status = useMemo(() => live?(effectiveToken&&effectiveToken.length>=32?'valid':'invalid'):lookupRecoveryToken(effectiveToken), [effectiveToken,live]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [touchedConfirm, setTouchedConfirm] = useState(false);
  const [pending,setPending]=useState(false),[error,setError]=useState('');
  const submittingRef=useRef(false);
  const successHeadingRef=useRef<HTMLHeadingElement>(null);

  useEffect(()=>{
    if(submitted) successHeadingRef.current?.focus();
  },[submitted]);

  const strength = passwordStrength(password);
  const requirementsMet = passwordMeetsAllRequirements(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmit = requirementsMet && passwordsMatch;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || submittingRef.current) return;
    // The default walkthrough stays UI-only; the live route uses the single-use backend token.
    if(!live){setSubmitted(true);return;}
    submittingRef.current=true;setPending(true);setError('');
    try{
      const response=await fetch('/api/v1/auth/recovery/complete',{method:'POST',credentials:'omit',headers:{Accept:'application/json','Content-Type':'application/json'},body:JSON.stringify({token:effectiveToken,new_password:password})});
      const body=await response.json();
      if(response.ok&&body.completed===true){setSubmitted(true);return;}
      if(response.status===400){setError('This reset link is invalid, expired, or already used. Request a new link and try again.');return;}
      if(response.status===429){setError('Too many reset attempts were made. Please wait before trying again.');return;}
      setError('Password reset is temporarily unavailable. Your link may still be valid; wait a moment and try again.');
    }
    catch{setError('Password reset is temporarily unavailable. Your link may still be valid; wait a moment and try again.');}
    finally{submittingRef.current=false;setPending(false);}
  }

  if (status !== 'valid') {
    const copy =
      status === 'expired'
        ? {
            eyebrow: 'LINK EXPIRED',
            heading: 'This link has expired',
            body: 'Password reset links are only valid for a limited time to keep your account secure. Request a new one and use it right away.',
          }
        : status === 'used'
          ? {
              eyebrow: 'LINK ALREADY USED',
              heading: 'This link has already been used',
              body: "Each reset link works once. If you didn't reset your password, request a new link now and consider it a good time to update your password.",
            }
          : {
              eyebrow: 'LINK NOT VALID',
              heading: "This link isn't valid",
              body: "This password reset link is broken, incomplete, or wasn't issued by ProFenz. Request a new one from the sign-in screen.",
            };
    return (
      <section aria-label="Password reset link problem" className="rpf-root">
        <HolmesJrGuide pose="guide" tip="Links like this only work once and expire on purpose — it's one of the ways we keep case files secure." />
        {!live&&<p className="rpf-flag" role="status">
          <strong>Local synthetic module</strong>
          <span>No real token was checked. This state is shown for demonstration.</span>
        </p>}
        <div className="rpf-problem">
          <p className="rpf-eyebrow">{copy.eyebrow}</p>
          <h1>{copy.heading}</h1>
          <p>{copy.body}</p>
          {onRequestNewLink ? (
            <button type="button" className="rpf-primary" onClick={onRequestNewLink}>
              Request a new link
            </button>
          ) : (
            <a className="rpf-primary rpf-primary-link" href={live?'/forgot-password':'/web/walkthrough/forgot-password'}>
              Request a new link
            </a>
          )}
        </div>
      </section>
    );
  }

  if (submitted) {
    return (
      <section aria-label="Password updated" className="rpf-root">
        <HolmesJrGuide pose="guide" tip="Sign in with your new password — this link won't work again." />
        <div className="rpf-problem">
          <p className="rpf-eyebrow">PASSWORD UPDATED</p>
          <h1 ref={successHeadingRef} tabIndex={-1}>Your password has been updated</h1>
          <p>Sign in with your new password.</p>
          <a className="rpf-primary rpf-primary-link" href="/sign-in">
            Go to sign in
          </a>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Choose a new password" className="rpf-root">
      <HolmesJrGuide pose="guide" tip="Choose a password you haven't used on ProFenz before." />
      {!live&&<p className="rpf-flag" role="status">
        <strong>Local synthetic module</strong>
        <span>Nothing typed here is sent, stored, or billed.</span>
      </p>}
      <header className="rpf-header">
        <p className="rpf-eyebrow">CHOOSE A NEW PASSWORD</p>
        <h1>Set a new password</h1>
      </header>
      <form className="rpf-form" onSubmit={submit} noValidate>
        {error&&<p className="notice error" role="alert">{error}</p>}
        <div className="rpf-field">
          <label htmlFor="rpf-password">New password</label>
          <input className="auth-light-input" id="rpf-password" type="password" autoComplete="new-password" required disabled={pending} value={password} onChange={(event) => { if (!submittingRef.current) setPassword(event.target.value); }} aria-describedby="rpf-requirements rpf-strength" />
        </div>
        <div className={`rpf-strength rpf-strength-${strength}`} id="rpf-strength" role="status">
          <span className="rpf-strength-bar" aria-hidden="true">
            <span />
          </span>
          <span className="rpf-strength-label">{password ? STRENGTH_LABEL[strength] : 'Strength'}</span>
        </div>
        <ul className="rpf-requirements" id="rpf-requirements" aria-label="Password requirements">
          {PASSWORD_REQUIREMENTS.map((requirement) => {
            const met = requirement.test(password);
            return (
              <li key={requirement.id} className={met ? 'is-met' : undefined}>
                <span aria-hidden="true">{met ? '✓' : '○'}</span>
                {requirement.label}
              </li>
            );
          })}
        </ul>
        <div className="rpf-field">
          <label htmlFor="rpf-confirm">Confirm new password</label>
          <input
            className="auth-light-input"
            id="rpf-confirm"
            type="password"
            autoComplete="new-password"
            required
            disabled={pending}
            value={confirmPassword}
            onChange={(event) => { if (!submittingRef.current) setConfirmPassword(event.target.value); }}
            onBlur={() => setTouchedConfirm(true)}
            aria-invalid={touchedConfirm && confirmPassword.length > 0 && !passwordsMatch ? 'true' : undefined}
          />
          {touchedConfirm && confirmPassword.length > 0 && !passwordsMatch && <span className="rpf-field-error">Passwords don't match.</span>}
        </div>
        <div className="rpf-actions">
          <button type="submit" className="rpf-primary" disabled={!canSubmit||pending}>
            {pending?'Updating…':'Update password'}
          </button>
        </div>
      </form>
    </section>
  );
}
